import { Form } from 'antd';
import { isArray, isEmpty, isPlainObject } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useAppConfig } from '@/config/context';
import { getParamFromUrl } from '@/infrastructure/history';
import { isAndroid } from '@/infrastructure/product-info';
import { toArray } from '@/infrastructure/utils/to-array';
import { getTableTimeQuickOptions, getTimeQuickOptions } from '@/presentation/components/exception-form/utils';
import { FormEntry, FormType, IFormRef, QueryForm } from '@/presentation/components/form';
import { NumberOperator } from '@/presentation/components/form/components/select-number';
import { VersionSelectGroupName } from '@/presentation/components/form/components/version-select-group';
import { CHARACTER_MATCH_ARRAY_OPTIONS, CHARACTER_MATCH_OPTIONS, MatchMode, NUMBER_MATCH_OPTIONS, VERSION_MATCH_OPTIONS } from '@/presentation/components/form/standards/types';
import { FilterEntry, FormColumnGroup } from '@/presentation/components/form/types';
import { onChangeAppVersion } from '@/presentation/components/form/utils';
import { hintText } from '@/presentation/components/hint-text/hintText';
import { enumFilterTips, numberFilterTips, stringArrayFilterTips, versionFilterTips } from '@/presentation/components/issue-list/components/tips';
import { EXCEPTION_TYPE_OPTIONS } from '@/presentation/pages/traffic/config';
import { SubType } from '@/presentation/pages/traffic/issues/list';
import { STANDARD_TRAFFIC_SIZE_TYPE_KEY } from '@/presentation/pages/traffic/issues/list/form/config';
import { FLOW_OPTIONS } from '@/presentation/pages/traffic/issues/list/table/flow';
import {
  AppVersionOperator,
  FIELD_ALL,
  FIELD_LABEL,
  FieldKey,
  Zone,
} from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { TimeType } from '@/proto/metric';
import { CompareFieldList, FieldList } from '@/proto/token';

interface IProps {
  onFinish: (value: Record<string, any>) => void;
  entry: FilterEntry;
  initialValue?: CompareFieldList & { timeRange: string[], isValueInit?: boolean };
  onValueInited?: (value: Record<string, any>) => void;
  subType?: string;
  tokenInitValues: FieldList & { isValueInit?: boolean };
}

const mb2Byte = (value: number | string): number => (Number(value) * 1024 * 1024);

export const  NetProblemForm: FC<IProps> = observer((props: IProps) => {
  const { tokenInitValues } = props;
  const [form] = Form.useForm();
  const tableRef = useRef<IFormRef>(null);
  const [, setShowSwitch] = useState(false);
  const trafficName = FLOW_OPTIONS.find(item => item.value === getParamFromUrl('trafficName'))?.label || '-' ;
  const trafficValue = getParamFromUrl('trafficValue');
  const config = useAppConfig();


  useEffect(() => {
    if (!isEmpty(tokenInitValues)) {
      const { isValueInit = true } = tokenInitValues;
      // 链接有token时，强制展示有value值的表单项
      tableRef?.current?.showFormItem?.(entries, tokenInitValues)?.then(() => {
        form.setFieldsValue(tokenInitValues);
        if (isValueInit) {
          const value = form.getFieldsValue();
          const { costTimeAvg, costTimeAvgCompare, parentTrafficSize, ...rest } = value;

          if (costTimeAvg || costTimeAvgCompare || parentTrafficSize) {
            props.onValueInited?.({
              costTimeAvgMin: costTimeAvg?.value[0],
              costTimeAvgMax: costTimeAvg?.value[1],
              costTimeAvgMinCompare: costTimeAvgCompare?.value[0],
              costTimeAvgMaxCompare: costTimeAvgCompare?.value[1],
              minParentTrafficSize: parentTrafficSize?.[0] ? mb2Byte(parentTrafficSize?.[0]) : undefined,
              maxParentTrafficSize: parentTrafficSize?.[1] ? mb2Byte(parentTrafficSize?.[1]) : undefined,
              ...rest,
            });
          } else {
            props.onValueInited?.(value);
          }
        }
      });
    }
  }, [tokenInitValues]);

  useEffect(() => {
    if (!isEmpty(props.initialValue)) {
      const { isValueInit = true } = props.initialValue;
      form.setFieldsValue(props.initialValue);
      if (isValueInit) {
        const value = form.getFieldsValue();
        const { costTimeAvg, costTimeAvgCompare, parentTrafficSize, ...rest } = value;

        if (costTimeAvg || costTimeAvgCompare || parentTrafficSize) {
          props.onValueInited?.({
            costTimeAvgMin: costTimeAvg?.value[0],
            costTimeAvgMax: costTimeAvg?.value[1],
            costTimeAvgMinCompare: costTimeAvgCompare?.value[0],
            costTimeAvgMaxCompare: costTimeAvgCompare?.value[1],
            minParentTrafficSize: parentTrafficSize?.[0] ? mb2Byte(parentTrafficSize?.[0]) : undefined,
            maxParentTrafficSize: parentTrafficSize?.[1] ? mb2Byte(parentTrafficSize?.[1]) : undefined,
            ...rest,
          });
        } else {
          props.onValueInited?.(value);
        }
      }
    }
  }, [props.initialValue]);

  const onFinish = (value: Record<string, any>) => {
    const { costTimeAvg, costTimeAvgCompare, parentTrafficSize, ...rest } = value;

    if (costTimeAvg || costTimeAvgCompare || parentTrafficSize) {
      props.onFinish({
        costTimeAvgMin: costTimeAvg?.value[0],
        costTimeAvgMax: costTimeAvg?.value[1],
        costTimeAvgMinCompare: costTimeAvgCompare?.value[0],
        costTimeAvgMaxCompare: costTimeAvgCompare?.value[1],
        minParentTrafficSize: parentTrafficSize?.[0] ? mb2Byte(parentTrafficSize?.[0]) : undefined,
        maxParentTrafficSize: parentTrafficSize?.[1] ? mb2Byte(parentTrafficSize?.[1]) : undefined,
        ...rest,
      });
    } else {
      props.onFinish(value);
    }
  };

  const isListPage = props.entry === FilterEntry.NetList;

  const entries: FormEntry[] = [
    {
      type: FormType.TimeRangeCascade,
      name: 'timeRange',
      label: FIELD_LABEL[FieldKey.TimeRange],
      labelString: FIELD_LABEL[FieldKey.TimeRange],
      scope: 'issues',
      deleteable: false,
      showTime: true,
      timeQuickOptions: isListPage
        ? undefined : getTimeQuickOptions(IssueType.ISSUE_TYPE_TRAFFIC, true),
      hiddenTimeWindow: !isListPage,
      isIssuesTimeWindow: isListPage,
      initTimeWindow: TimeType.MINUTE5,
      limitDays: 6,
      getTimeQuickOptions: isListPage
        ? timeWindow => (getTableTimeQuickOptions(
          IssueType.ISSUE_TYPE_TRAFFIC,
          true, timeWindow,
        )) : undefined,
      disabled: props.subType === SubType.trafficProblem,
    },
    {
      type: FormType.TimeRangeCascade,
      name: 'timeRange',
      initTimeWindow: form.getFieldValue('timeWindow') || TimeType.HOUR,
      label: FIELD_LABEL[FieldKey.TimeRange],
      scope: 'analytics',
      deleteable: false,
      showTime: false,
      hiddenTimeWindow: true,
      hiddenTimeQuick: true,
      limitDays: 6,
      isNotRange: true,
      disabled: props.subType !== SubType.trafficProblem,
    },
    {
      type: FormType.VersionSelectGroup,
      leftName: FieldKey.AppVersionOperator,
      rightName: FieldKey.AppVersion,
      label: FIELD_LABEL[FieldKey.AppVersionOperator],
      leftFieldKey: FieldKey.StandardAppVersionOperator,
      rightFieldKey: FieldKey.AppVersion,
      fieldScope: Zone.Net,
      scope: 'issues',
      mode: 'tags',
      leftInitialValue: AppVersionOperator.Or,
      rightInitialValue: [FIELD_ALL],
      name: VersionSelectGroupName.appVersionGroup,
      deleteable: false,
      hasMaxWidth: true,
      group: FormColumnGroup.ApplicationInfo,
    },
    {
      type: FormType.Select,
      name: FieldKey.CollectType,
      label: hintText(FIELD_LABEL[FieldKey.CollectType], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.CollectType],
      fieldKey: FieldKey.CollectType,
      fieldScope: Zone.Net,
      mode: 'single',
      initialValue: FIELD_ALL,
      group: FormColumnGroup.ContextInfo,
      options: [
        { value: '', label: '所有' },
        { value: 'auto', label: '通用统计' },
        { value: 'custom', label: '业务统计' },
      ],
    },
    {
      type: FormType.Select,
      name: FieldKey.ExceptionType,
      label: hintText(FIELD_LABEL[FieldKey.ExceptionType], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.ExceptionType],
      fieldKey: FieldKey.ExceptionType,
      fieldScope: Zone.Net,
      mode: 'single',
      initialValue: FIELD_ALL,
      options: EXCEPTION_TYPE_OPTIONS,
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardOsVersion,
      label: hintText(FIELD_LABEL[FieldKey.OsVersion], versionFilterTips),
      labelString: FIELD_LABEL[FieldKey.OsVersion],
      group: FormColumnGroup.DeviceInfo,
      fieldKey: FieldKey.OsVersion,
      fieldScope: Zone.Net,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: VERSION_MATCH_OPTIONS,
    },
    {
      type: FormType.Input,
      name: 'clientIdentify',
      group: FormColumnGroup.exceptionInfo,
      label: '消息ID',
      disabled: props.entry === FilterEntry.NetList,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardBundleId,
      group: FormColumnGroup.ApplicationInfo,
      label: FIELD_LABEL[FieldKey.BundleId],
      fieldKey: FieldKey.BundleId,
      fieldScope: Zone.Net,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardSdkVersion,
      group: FormColumnGroup.ApplicationInfo,
      label: hintText(FIELD_LABEL[FieldKey.SdkVersion], versionFilterTips),
      labelString: FIELD_LABEL[FieldKey.SdkVersion],
      fieldKey: FieldKey.SdkVersion,
      fieldScope: Zone.Net,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardModel',
      group: FormColumnGroup.DeviceInfo,
      label: '机型',
      labelString: '机型',
      fieldKey: FieldKey.Model,
      fieldScope: Zone.Net,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      group: FormColumnGroup.ContextInfo,
      name: 'standardProcessName',
      label: '进程',
      labelString: '进程',
      mode: 'tags',
      fieldKey: FieldKey.ProcessName,
      fieldScope: Zone.Net,
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.Input,
      name: FieldKey.Operator,
      label: FIELD_LABEL[FieldKey.Operator],
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardAccountId,
      group: FormColumnGroup.UserInfo,
      isSplit: true,
      label: '用户ID',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardQimei,
      label: '设备ID',
      group: FormColumnGroup.UserInfo,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.Input,
      name: 'launchId',
      group: FormColumnGroup.ContextInfo,
      label: FIELD_LABEL[FieldKey.LaunchId],
      labelString: '启动ID',
    },
    {
      type: FormType.Input,
      name: 'processLaunchId',
      label: FIELD_LABEL[FieldKey.ProcessLaunchId],
      labelString: '进程启动ID',
      group: FormColumnGroup.ContextInfo,
      disabled: !isAndroid(),
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardShiplyTag,
      label: hintText(FIELD_LABEL[FieldKey.ShiplyTag], stringArrayFilterTips),
      labelString: FIELD_LABEL[FieldKey.ShiplyTag],
      fieldKey: FieldKey.TestTag,
      fieldScope: Zone.Net,
      group: FormColumnGroup.Custom,
      mode: 'tags',
      disabled: !config.enableShiplyAbTestTag,
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardAbTestTag,
      group: FormColumnGroup.Custom,
      label: hintText(FIELD_LABEL[FieldKey.AbTestTag], stringArrayFilterTips),
      labelString: FIELD_LABEL[FieldKey.AbTestTag],
      fieldKey: FieldKey.AbTestTagLine,
      fieldScope: Zone.Net,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
    },
    {
      type: FormType.CustomFields,
      group: FormColumnGroup.Custom,
      name: FieldKey.UserCustom,
      label: '自定义维度',
    },
    {
      type: FormType.SelectNumberGroup,
      name: 'parentTrafficSize',
      group: FormColumnGroup.exceptionInfo,
      label: hintText('异常流量(MB)', numberFilterTips),
      labelString: '异常流量(MB)',
      options: [{ value: NumberOperator.RANGE, label: '范围' }],
      processValue: (result) => {
        if (isArray(result)) {
          return { parentTrafficSize: result };
        }
        return ({ parentTrafficSize: result.value });
      },
      convertValue: (value) => {
        if (isPlainObject(value)) return value;
        return {
          operator: NumberOperator.RANGE,
          value,
        };
      },
      disabled: props.subType !== SubType.trafficProblem,
    },
    {
      type: FormType.CustomFields,
      name: FieldKey.StandardTrafficSizeType,
      group: FormColumnGroup.exceptionInfo,
      label: '异常流量',
      keys: STANDARD_TRAFFIC_SIZE_TYPE_KEY,
      disabled: props.subType !== SubType.issueAnalytics,
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.TrafficSize,
      label: hintText('消耗流量(MB)', `当前归因维度消耗的流量，本页指「${trafficValue}」这个${trafficName}消耗的流量`),
      labelString: '消耗流量(MB)',
      options: NUMBER_MATCH_OPTIONS,
      disabled: props.entry === FilterEntry.NetList,
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.TdsArea,
      name: FieldKey.StandardArea,
      label: '地域',
      labelString: '地域',
      fieldScope: Zone.Net,
      compareDisabled: true,
      initialDeleted: true,
      group: FormColumnGroup.UserInfo,
    },
  ];

  return <QueryForm
    form={form}
    cachePrefix={'prefix1'}
    ref={tableRef}
    entries={entries}
    onFinish={onFinish}
    onChange={async (__, fields) => onChangeAppVersion({
      values: toArray(fields.appVersionGroup ?? ''),
      operator: fields?.appVersionOperator ?? '',
    }, setShowSwitch)}
    onReset={() => setShowSwitch(false)}
    pageEntry={props.entry}
    issueType={IssueType.ISSUE_TYPE_TRAFFIC}
    hasCompare={props.entry === FilterEntry.DropFrameList}
    defaultValues={props.initialValue}
    renderByGroup
  />;
});
